/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { Box, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import {
  setBofuTabRepresentanteDocument,
  setInformationRepresentanteId,
  setParamsIdentityValidationForm,
} from "@store/slices/bofuSlice";
import TabBofu from "@components/TabBofu/TabBofu";
import { InactivityHandler } from "@components/InactivityHandler";
import { BOFU_TABS_CONFIG } from "@helpers/constantes";
import ButtonExpedienteStatus from "@components/ButtonExpedienteStatus";
import { BofuTabPropietario, BofuTabRepresentante } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import DocumentsTabBofu from "@components/TabBofu/DocumentsTabBofu";
import ButtonContinue from "@components/TabBofu/ButtonContinue";
import { CustomCompanyName } from "@components/CustomCompanyName";

const PropietarioRealPM: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const bufuLoading = useSelector((state: RootState) => state.bofu.isLoading);
  const bofuTabsObj = useSelector((state: RootState) => state.bofu.bofu_tabs_obj);

  const {
    informacion_representante_legal: showInformacionRepresentante,
    validacion_identidad: showValidacionIdentidad,
    informacion_bancaria: showInformacionBancaria,
  } = bofuTabsObj.representantes.forms;
  const showAnyRepresentanteForm = showInformacionRepresentante || showValidacionIdentidad || showInformacionBancaria;

  const handleClickExpedient = (representanteItem: BofuTabPropietario) => {
    dispatch(setInformationRepresentanteId(representanteItem.persona.id));
    setTimeout(() => {
      navigate("/producto/creditosimple/informacion_representante");
    }, 200);
  };

  const handleClickBankInformation = (representanteItem: BofuTabRepresentante) => {
    navigate(`/producto/creditosimple/info_bancaria_representante/${representanteItem.persona.id}`);
  };

  const handleClickIdentityValidation = (representanteItem: BofuTabRepresentante) => {
    dispatch(
      setParamsIdentityValidationForm({
        person_id: representanteItem.persona.id,
        person_type: "REP_LEGAL",
      }),
    );
    setTimeout(() => {
      navigate("/producto/creditosimple/validacion_identidad_representante");
    }, 200);
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <Box textAlign={"center"} mx={4}>
          <InactivityHandler />

          <CustomCompanyName />

          <TabBofu />

          {bufuLoading ? (
            <Box mt={3}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            </Box>
          ) : (
            <Box>
              {bofuTabsObj.representantes.items.length === 0 && (
                <Typography
                  color={theme?.palette?.primary?.dark}
                  fontSize={"20px"}
                  textAlign={"center"}
                  fontWeight={600}
                >
                  No se encontraron representantes legales registrados
                </Typography>
              )}

              {showAnyRepresentanteForm && (
                <Typography
                  mt={4}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  fontWeight={700}
                  color={theme?.palette?.primary?.dark}
                >
                  Ahora llena los formatos relacionados al o los representantes legales
                </Typography>
              )}

              {bofuTabsObj.representantes.items.map((representanteItem: BofuTabRepresentante, index: number) => (
                <Box key={index} mt={3}>
                  <Typography
                    mt={2}
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={theme?.palette?.primary?.main}
                  >
                    Representante legal {index + 1}:{" "}
                    {`${representanteItem.persona.name || ""} ${representanteItem.persona.last_name || ""} ${
                      representanteItem.persona.last_name_2 || ""
                    }`}
                  </Typography>

                  {representanteItem.show_information_form && showInformacionRepresentante && (
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant="body1"
                        sx={{ flex: 1, textAlign: "left" }}
                        color={theme?.palette?.primary?.dark}
                      >
                        Información del representante legal
                      </Typography>

                      <ButtonExpedienteStatus
                        mode="form"
                        formStatus={representanteItem.persona.personal_information_status}
                        onClick={() => handleClickExpedient(representanteItem)}
                      />
                    </Box>
                  )}

                  {showInformacionBancaria && (
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant="body1"
                        sx={{ flex: 1, textAlign: "left" }}
                        color={theme?.palette?.primary?.dark}
                      >
                        Información bancaria
                      </Typography>

                      <ButtonExpedienteStatus
                        mode="form"
                        formStatus={representanteItem.persona.bank_references_status || null}
                        onClick={() => handleClickBankInformation(representanteItem)}
                      />
                    </Box>
                  )}

                  {showValidacionIdentidad && (
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant="body1"
                        sx={{ flex: 1, textAlign: "left" }}
                        color={theme?.palette?.primary?.dark}
                      >
                        Validación de identidad
                      </Typography>

                      <ButtonExpedienteStatus
                        mode="validation"
                        isCompleted={representanteItem.persona.identity_validation_completed}
                        documentStatus={representanteItem.doc_validacion_identidad?.validated}
                        onClick={() => handleClickIdentityValidation(representanteItem)}
                      />
                    </Box>
                  )}

                  {showValidacionIdentidad && (
                    <Typography
                      mt={4}
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      color={theme?.palette?.primary?.dark}
                    >
                      Necesitarás tener la identificación a la mano y tomar un par de selfies
                    </Typography>
                  )}

                  <DocumentsTabBofu
                    inputPrefix={`propietario_${index}`}
                    tabCode={BOFU_TABS_CONFIG.tab_propietario.code}
                    documents={representanteItem.documents || []}
                    onChange={({ document, newValue }) => {
                      dispatch(
                        setBofuTabRepresentanteDocument({
                          document,
                          newValue,
                          persona: representanteItem.persona,
                          valueAction: undefined,
                          valueIndex: undefined,
                        }),
                      );
                    }}
                    onChangeMultiple={({ document, newValue, valueAction, valueIndex }) => {
                      dispatch(
                        setBofuTabRepresentanteDocument({
                          document,
                          newValue,
                          persona: representanteItem.persona,
                          valueAction: valueAction || undefined,
                          valueIndex,
                        }),
                      );
                    }}
                    RFC={representanteItem.persona.rfc}
                  />
                </Box>
              ))}

              <Box mt={4}>
                <ButtonContinue tabCode={BOFU_TABS_CONFIG.tab_representante.code} />
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default PropietarioRealPM;
